import { Add, Delete, Edit } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import AlertDialog from "components/AlertDialog";
import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Dialog from "components/NoScrollDialog";
import { INTERNAL } from "const";
import { useLanguage } from "contexts/language.context";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import areasService from "services/areas-service";
import auditsService from "services/audits-service";
import branchesService from "services/branches-service";
import useOpen from "utils/useOpen";
import AreaDialog from "./AreaDialog";
import { usePermissionMonitor } from "components/PermissionMonitor";

export default function AuditsConsolidatedReportSettings({ data, setData }) {
  const branchGroupId = data.branchGroup.id;
  const { label } = useLanguage();
  const [selected, setSelected] = useState(data.originalSelected || []);
  const [branchGroup, setBranchGroup] = useState(data.newBranchGroup);
  const [loading, setLoading] = useState({
    fetchAudits: 1,
    fetchReports: 0,
  });
  const [areas, setAreas] = useState(null);
  const [tab, setTab] = useState("audits");
  const mounted = useRef(false);
  const reportRequest = useRef(0);
  const [reportError, setReportError] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      reportRequest.current += 1;
    };
  }, []);
  const reportsReady =
    selected.flat().length > 0 &&
    selected
      .flat()
      .every((id) =>
        branchGroup?.branches?.some((branch) =>
          branch.audits.some(
            (audit) => audit.id === id && audit.reportGenerated,
          ),
        ),
      );

  useEffect(() => {
    if (tab == "audits") {
      fetchAudits(false);
    }
    getAreas();
  }, [tab]);

  useEffect(() => {
    if (!selected.length && data.originalSelected) {
      setSelected(data.originalSelected);
    }
  }, [data.originalSelected]);

  useEffect(() => {
    if (!branchGroup && data.newBranchGroup) {
      setBranchGroup(data.newBranchGroup);
    }
  }, [data.newBranchGroup]);

  const fetchAudits = async (load = true) => {
    if (load)
      setLoading((curr) => ({
        ...curr,
        fetchAudits: (curr.fetchAudits || 0) + 1,
      }));
    const [response, data] = await branchesService.getBranches({
      branchGroupId: branchGroupId,
      filterAuditsByGeneratedReports: true,
      includeColorPalette: true,
    });

    if (!mounted.current) return;
    if (response.ok) {
      const _branchGroup = data[0];
      setBranchGroup((current) => {
        const reportsByAuditId = new Map(
          current?.branches?.flatMap((branch) =>
            branch.audits.map((audit) => [audit.id, audit.reportGenerated]),
          ) || [],
        );

        return {
          ..._branchGroup,
          branches: _branchGroup.branches.map((branch) => ({
            ...branch,
            audits: branch.audits.map((audit) => ({
              ...audit,
              reportGenerated:
                audit.reportGenerated || reportsByAuditId.get(audit.id),
            })),
          })),
        };
      });
      if (selected.length && selected.find((s) => s.length)) {
      } else {
        setSelected(() => {
          return _branchGroup.branches.map((branch) => {
            return [];
          });
        });
      }
    }

    setLoading((curr) => ({
      ...curr,
      fetchAudits:
        curr.fetchAudits <= 0 ? curr.fetchAudits : curr.fetchAudits - 1,
    }));
  };

  const fetchAuditReports = async () => {
    const request = ++reportRequest.current;
    if (!selected.flat().length || !branchGroup?.branches) return;
    const selectedIds = new Set(selected.flat());
    const required = branchGroup.branches
      .flatMap((branch) => branch.audits)
      .filter((audit) => selectedIds.has(audit.id) && !audit.reportGenerated)
      .map((audit) => audit.id);
    if (!required.length) {
      setReportError(false);
      setLoading((current) => ({ ...current, fetchReports: 0 }));
      return;
    }
    setReportError(false);
    setLoading((current) => ({ ...current, fetchReports: 1 }));
    try {
      const [response, reports] =
        await auditsService.getAssessmentReportsDataForConsolidated({
          auditIds: required,
        });
      if (!mounted.current || reportRequest.current !== request) return;
      if (
        !response.ok ||
        !Array.isArray(reports) ||
        required.some(
          (id) =>
            !reports.some((audit) => audit.id === id && audit.reportGenerated),
        )
      ) {
        setReportError(true);
        return;
      }
      setBranchGroup((current) => ({
        ...current,
        branches: current.branches.map((branch) => ({
          ...branch,
          audits: branch.audits.map((audit) => {
            const loaded = reports.find((item) => item.id === audit.id);
            return loaded ? { ...audit, ...loaded, type: audit.type } : audit;
          }),
        })),
      }));
    } catch {
      if (mounted.current && reportRequest.current === request)
        setReportError(true);
    } finally {
      if (mounted.current && reportRequest.current === request)
        setLoading((current) => ({ ...current, fetchReports: 0 }));
    }
  };

  async function getAreas() {
    setLoading((loading) => ({ ...loading, fetchAreas: true }));
    const [response, data] = await areasService.getAreas({ branchGroupId });
    if (!mounted.current) return;
    if (response.ok) {
      setAreas(data);
    }

    setLoading((loading) => ({ ...loading, fetchAreas: false }));
  }

  const getBranches = useCallback(() => {
    return (
      branchGroup?.branches
        ?.map((branch, index) => {
          if (selected[index]?.length > 0) {
            const audits = branch.audits.filter((audit) =>
              selected[index].includes(audit.id),
            );
            return { ...branch, audits };
          } else {
            return { ...branch, audits: [] };
          }
        })
        ?.filter((branch) => branch.audits.length > 0) || []
    );
  }, [selected, branchGroup]);

  const continueAction = useCallback(
    (next) => (
      <MDButton
        variant="gradient"
        color="info"
        disabled={!reportsReady || loading.fetchReports || loading.fetchAudits}
        onClick={() => {
          setData((current) => ({
            ...current,
            branches: getBranches(),
            selected: branchGroup.branches.map((branch, index) => ({
              id: branch.id,
              selected: selected[index] || [],
            })),
            newBranchGroup: branchGroup,
            originalSelected: selected,
            reportAreas: areas,
          }));
          next();
        }}
      >
        {loading.fetchReports || loading.fetchAudits
          ? label("loading")
          : label("next-button")}
      </MDButton>
    ),
    [
      reportsReady,
      loading,
      setData,
      getBranches,
      branchGroup,
      selected,
      areas,
      label,
    ],
  );

  useEffect(() => {
    fetchAuditReports();
  }, [selected, Boolean(branchGroup)]);

  useEffect(() => {
    setData((data) => ({ ...data, continueAction }));
  }, [continueAction]);

  return (
    <>
      {reportError && (
        <Alert
          severity="error"
          action={
            <MDButton type="button" onClick={fetchAuditReports}>
              {label("consolidated-report-retry") || "Retry"}
            </MDButton>
          }
        >
          {label("consolidated-report-load-error") ||
            "Some selected audit reports could not be loaded. Retry before continuing."}
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, pb: 2 }}>
        <MDButton
          variant={tab == "audits" ? "gradient" : "outlined"}
          color="info"
          sx={{ flex: 1 }}
          onClick={() => setTab("audits")}
        >
          {label("audit-plural")}
        </MDButton>
        <MDButton
          sx={{ flex: 1 }}
          variant={tab == "areas" ? "gradient" : "outlined"}
          color="info"
          onClick={() => setTab("areas")}
        >
          {label("area-plural")}
        </MDButton>
      </Box>
      {tab == "audits" ? (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Grid container>
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <Collapse style={{ width: "100%" }} in={!loading.fetchAudits}>
                  {branchGroup?.branches.map((branch, index) => {
                    return (
                      <ListItem
                        branch={branch}
                        selected={selected}
                        branchIndex={index}
                        setSelected={setSelected}
                        loading={loading}
                      />
                    );
                  })}
                </Collapse>
              </Grid>

              {loading.fetchAudits ? (
                <Grid
                  item
                  xs={12}
                  style={{
                    width: "100%",
                    height: "200px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress
                    sx={{ margin: "0 auto" }}
                    color="info"
                    size={30}
                    thickness={6}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </MDBox>
      ) : null}
      {tab == "areas" ? (
        <AreasTab
          areas={areas}
          getAreas={getAreas}
          branchGroupId={branchGroupId}
        />
      ) : null}
    </>
  );
}

function AreasTab({ areas, getAreas, branchGroupId }) {
  const [loading, setLoading] = useState({ fetch: false });
  const { label } = useLanguage();
  const open = useOpen();
  const [branches, setBranches] = useState(null);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [currentArea, setCurrentArea] = useState(null);
  const can = usePermissionMonitor();

  async function getBranches() {
    setLoading((loading) => ({ ...loading, fetchBranches: true }));
    const [response, data] = await branchesService.getBranches({
      branchGroupId,
    });
    if (response.ok) {
      setBranches(data[0].branches);
    }
    setLoading((loading) => ({ ...loading, fetchBranches: false }));
  }

  async function createArea(area) {
    setLoading((loading) => ({ ...loading, create: true }));
    const [response, data] = await areasService.createArea(area);

    if (response.ok) {
      await getAreas();
      setLoading((loading) => ({ ...loading, create: false }));
      return { success: true };
    } else {
      setLoading((loading) => ({ ...loading, create: false }));
      return { error: data.error };
    }
  }

  async function updateBranchArea(branchId, areaId) {
    setLoading((loading) => ({ ...loading, update: true }));
    if (!areaId) {
      const oldArea = areas.find((area) =>
        area.branches.find((branch) => branch.id == branchId),
      );

      if (!oldArea) return;

      const [response, data] = await areasService.updateArea(oldArea.id, {
        branches: oldArea.branches
          .filter((branch) => branch.id != branchId)
          .map((v) => ({ id: v.id })),
      });
      if (response.ok) {
        Promise.all([getAreas(), getBranches()]);
      }
    } else {
      const [response, data] = await areasService.updateArea(areaId, {
        branches: [...branchesByArea[areaId], { id: branchId }],
      });
      if (response.ok) {
        Promise.all([getAreas(), getBranches()]);
      }
    }
    setLoading((loading) => ({ ...loading, update: false }));
  }

  async function updateArea(areaId, area) {
    setLoading((loading) => ({ ...loading, update: true }));
    const [response, data] = await areasService.updateArea(areaId, area);
    if (response.ok) {
      await getAreas();
      setLoading((loading) => ({ ...loading, update: false }));
      return { success: true };
    } else {
      setLoading((loading) => ({ ...loading, update: false }));
      return { error: data.error };
    }
  }

  async function deleteArea(areaId) {
    const [response, data] = await areasService.removeArea(areaId);
    if (response.ok) {
      Promise.all([getAreas(), getBranches()]);
      setLoading((loading) => ({ ...loading, update: false }));
      return { success: true };
    } else {
      setLoading((loading) => ({ ...loading, update: false }));
      return { error: data.error };
    }
  }

  const onDrag = (branch) => (e) => {
    setCurrentBranch(branch);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = () => (e) => {
    e.preventDefault();
    updateBranchArea(currentBranch.id, currentArea?.id);
  };

  const onDragOver = (area) => (e) => {
    setCurrentArea(area);
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const branchesByArea = useMemo(() => {
    if (!areas || !branches) return {};

    const result = {};
    areas?.map((area) => {
      result[area.id] = branches.filter((branch) => branch?.areaId == area.id);
    });

    result["none"] = branches.filter(
      (branch) => !areas?.map((s) => s.id).includes(branch?.areaId),
    );
    return result;
  }, [branches, areas]);

  useEffect(() => {
    Promise.all([getAreas(), getBranches()]);
  }, []);

  return (
    <>
      {open.get("dialog") ? (
        <AreaDialog
          isOpen={open.get("dialog")}
          onSubmit={(area) => {
            if (open.get("dialog").type == "create") {
              return createArea(area);
            } else {
              return updateArea(area.id, area);
            }
          }}
          data={open.get("dialog")?.data}
          type={open.get("dialog")?.type}
          branchGroupId={branchGroupId}
        />
      ) : null}

      {open.get("alert") ? (
        <AlertDialog
          open={open.get("alert")}
          setOpen={(value) => {
            open.change("alert", value);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await deleteArea(open.get("alert")?.data);
          }}
        />
      ) : null}

      {loading.fetchAreas || loading.fetchBranches ? (
        <div
          style={{
            // height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="info" />
        </div>
      ) : (
        <Grid container spacing={2}>
          {can("create", "area") ? (
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => {
                  open.change("dialog", { type: "create" });
                }}
              >
                <Add />
                &nbsp; {label("create-new-area-button")}
              </MDButton>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Card
              sx={{
                px: 1.5,
                py: 1,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              }}
              variant="outlined"
              onDrop={onDrop()}
              onDragOver={onDragOver(null)}
            >
              <MDTypography variant="h6" sx={{ marginBottom: 1 }}>
                {label("branches-with-no-area")}
              </MDTypography>
              <Grid container spacing={1}>
                {branchesByArea["none"]?.length > 0 ? (
                  branchesByArea["none"]?.map((branch) => {
                    return (
                      <Grid key={branch.id} item>
                        <MDBadge
                          size="lg"
                          badgeContent={
                            <>
                              <Icon
                                fontSize="medium"
                                sx={{
                                  margin: "-10px",
                                  marginLeft: "-16px",
                                  marginRight: "0px",
                                }}
                              >
                                drag_indicator
                              </Icon>
                              {branch.name}
                            </>
                          }
                          variant="gradient"
                          color="primary"
                          container
                          sx={{ cursor: "move" }}
                          draggable={Boolean(can("update", "area"))}
                          onDrag={
                            can("update", "area") ? onDrag(branch) : () => {}
                          }
                        />
                      </Grid>
                    );
                  })
                ) : (
                  <MDTypography
                    variant="caption"
                    sx={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "rgba(0, 0, 0, 0.5)",
                      padding: "0px 8px",
                      paddingTop: "8px",
                    }}
                  >
                    {label("no-branches-with-no-area")}
                  </MDTypography>
                )}
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12} sx={{ mb: -1 }}>
            <MDTypography
              variant="h6"
              sx={{ marginTop: 2, marginBottom: 0, pb: 0 }}
            >
              {label("area-plural")}
            </MDTypography>
          </Grid>
          {!areas?.length ? (
            <MDTypography
              variant="caption"
              sx={{
                fontSize: "14px",
                fontWeight: "400",
                color: "rgba(0, 0, 0, 0.5)",
                padding: "0px 16px",
                paddingTop: "8px",
              }}
            >
              {label("no-areas-were-found")}
            </MDTypography>
          ) : (
            ""
          )}
          {areas?.map((area) => (
            <Grid alignItems="stretch" item xs={6} key={area.id}>
              <Card
                sx={{
                  px: 1.5,
                  py: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                }}
                variant="outlined"
                onDrop={onDrop()}
                onDragOver={onDragOver(area)}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <MDTypography variant="h6" sx={{ marginBottom: 1 }}>
                    {area.name}
                  </MDTypography>
                  <Box>
                    {can("update", "area") ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          open.change("dialog", { type: "edit", data: area });
                        }}
                      >
                        <Edit />
                      </IconButton>
                    ) : null}
                    {can("delete", "area") ? (
                      <IconButton size="small">
                        <Delete
                          onClick={() => {
                            open.change("alert", { data: area.id });
                          }}
                        />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>

                <Grid container spacing={1}>
                  {branchesByArea[area.id]?.length > 0 ? (
                    branchesByArea[area.id]?.map((branch) => {
                      return (
                        <Grid key={branch.id} item>
                          <MDBadge
                            size="lg"
                            badgeContent={
                              <>
                                <Icon
                                  fontSize="medium"
                                  sx={{
                                    margin: "-10px",
                                    marginLeft: "-16px",
                                    marginRight: "0px",
                                  }}
                                >
                                  drag_indicator
                                </Icon>
                                {branch.name}
                              </>
                            }
                            variant="gradient"
                            color="primary"
                            container
                            sx={{ cursor: "move" }}
                            draggable={Boolean(can("update", "area"))}
                            onDrag={
                              can("update", "area") ? onDrag(branch) : () => {}
                            }
                          />
                        </Grid>
                      );
                    })
                  ) : (
                    <MDTypography
                      variant="caption"
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "rgba(0, 0, 0, 0.5)",
                        padding: "0px 8px",
                        paddingTop: "8px",
                      }}
                    >
                      {label("no-branches-in-area")}
                    </MDTypography>
                  )}
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
}

function ListItem({ branch, selected, branchIndex, setSelected, loading }) {
  const { label } = useLanguage();
  const auditsByDate = [...(branch?.audits || [])].sort((left, right) => {
    const leftDate = left.date ? Date.parse(left.date) : Infinity;
    const rightDate = right.date ? Date.parse(right.date) : Infinity;
    return leftDate - rightDate || left.id - right.id;
  });

  return (
    <MDBox sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          width: "100%",
          fontWeight: "500",
          fontSize: "16px",
          py: 1,
        }}
      >
        <span>{branch?.name}</span>
        <Box>
          <MDBadge badgeContent={branch?.region.description} />
          {branch?.area ? (
            <MDBadge badgeContent={branch?.area?.name} color="primary" />
          ) : null}
        </Box>
      </Box>

      <MDBox sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            m: 0,
            px: 1.5,
            overflow: "hidden",
          }}
          variant="outlined"
        >
          <MDBox
            sx={(theme) => ({
              borderBottom: "1px solid",
              borderColor: theme.borders.borderColor,
              backgroundColor: theme.palette.info.main + "22",
              mx: -1.5,
              px: 1.5,
              py: 1,
            })}
          >
            <MDTypography variant="h6">{label("internal")}</MDTypography>
          </MDBox>
          <CardContent sx={{ p: 1, m: 0 }}>
            {auditsByDate
              .filter((audit) => audit.type == INTERNAL)
              .map((audit) => {
                return (
                  <FormControlLabel
                    key={"Audit: " + audit.id}
                    sx={{
                      marginTop: 0,
                      marginBottom: -1,
                    }}
                    label={audit?.description}
                    control={
                      <Checkbox
                        disabled={Boolean(loading?.fetchReports)}
                        checked={selected[branchIndex]?.includes(audit.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelected((curr) => {
                              const newSelected = curr.map((ids) => [...ids]);
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id,
                              );

                              if (index == -1) {
                                newSelected[branchIndex].push(audit.id);
                              }

                              return newSelected;
                            });
                          } else {
                            setSelected((curr) => {
                              const newSelected = curr.map((ids) => [...ids]);
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id,
                              );

                              newSelected[branchIndex].splice(index, 1);
                              return newSelected;
                            });
                          }
                        }}
                      />
                    }
                  />
                );
              })}
          </CardContent>
        </Card>
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            m: 0,
            px: 1.5,
            overflow: "hidden",
          }}
          variant="outlined"
        >
          <MDBox
            sx={(theme) => ({
              borderBottom: "1px solid",
              borderColor: theme.borders.borderColor,
              backgroundColor: theme.palette.primary.main + "22",
              mx: -1.5,
              px: 1.5,
              py: 1,
            })}
          >
            <MDTypography variant="h6">{label("external")}</MDTypography>
          </MDBox>
          <CardContent sx={{ p: 1, m: 0 }}>
            {auditsByDate
              .filter((audit) => audit.type != INTERNAL)
              .map((audit) => {
                return (
                  <FormControlLabel
                    key={"Audit: " + audit.id}
                    sx={{
                      marginTop: 0,
                      marginBottom: -1,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 42px)",
                      },
                    }}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          {audit?.description}
                        </Typography>
                        {audit.type == 3 ? (
                          <MDBadge
                            color="primary"
                            badgeContent={label("secondary-auditor")}
                          />
                        ) : null}
                      </Box>
                    }
                    fullWidth
                    control={
                      <Checkbox
                        disabled={Boolean(loading?.fetchReports)}
                        checked={selected[branchIndex]?.includes(audit.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelected((curr) => {
                              const newSelected = curr.map((ids) => [...ids]);
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id,
                              );

                              if (index == -1) {
                                newSelected[branchIndex].push(audit.id);
                              }

                              return newSelected;
                            });
                          } else {
                            setSelected((curr) => {
                              const newSelected = curr.map((ids) => [...ids]);
                              const index = newSelected[branchIndex].findIndex(
                                (value) => value == audit.id,
                              );

                              newSelected[branchIndex].splice(index, 1);
                              return newSelected;
                            });
                          }
                        }}
                      />
                    }
                  />
                );
              })}
          </CardContent>
        </Card>
      </MDBox>
      <Divider />
    </MDBox>
  );
}
